import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { Button as BaseButton, ContextMenu, FlexMiddle } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import { formatDate } from '../leaveFormatters';

const HeaderRow = styled(FlexMiddle)`
  flex-wrap: wrap;
  gap: 12px;
  padding: 4px 0 0;
  justify-content: flex-start;
`;

const LedgerHistoryButton = styled(BaseButton)`
  flex-shrink: 0;
`;

const HeaderSpacer = styled.div`
  flex: 1;
`;

const ContractChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  background: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  font: inherit;
  color: ${({ theme }) => theme.colors.black};
  font-weight: 500;
  transition: background 0.15s ease;

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.grey1};
  }
`;

const ContractName = styled.span`
  font-size: 13px;
  line-height: 18px;
`;

const AssignmentDate = styled.span`
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.grey4};
`;

const FteChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  background: ${({ theme }) => theme.colors.white};
  font-size: 13px;
  line-height: 18px;
  color: ${({ theme }) => theme.colors.black};
`;

const FteLabel = styled.span`
  color: ${({ theme }) => theme.colors.grey4};
  font-size: 12px;
`;

const FteValue = styled.span`
  font-weight: 500;
`;

const AssignButton = styled(BaseButton)`
  padding: 6px 14px;
  line-height: 20px;
  font-weight: 500;
`;

const ContractHeaderChip = ({
  management,
  onChange,
  onTerminate,
  onYearReset,
  onPayoutAll,
  onEditAssignment,
  hasBalances,
  hasManualYearCycleBalance
}) => {
  const { t } = useContext(ConfigContext);
  const formattingMap = useFormattingMap();

  const contractName = management?.contractName || management?.accountContractV2Name || '';
  const assignmentDate = formatDate(formattingMap, management?.startDate);

  const options = useMemo(() => {
    const items = [
      { label: t('employeeLeave_contractActions_editAssignment'), onClick: onEditAssignment },
      { label: t('employeeLeave_contractActions_change'), onClick: onChange },
      { label: t('employeeLeave_contractActions_terminate'), onClick: onTerminate }
    ];
    if (hasManualYearCycleBalance) {
      items.push({
        label: t('employeeLeave_contractActions_yearReset'),
        onClick: onYearReset,
        disabled: !hasBalances
      });
    }
    items.push({
      label: t('employeeLeave_contractActions_payoutAll'),
      onClick: onPayoutAll,
      disabled: !hasBalances
    });
    return items;
  }, [t, onEditAssignment, onChange, onTerminate, onYearReset, onPayoutAll, hasBalances, hasManualYearCycleBalance]);

  return (
    <ContextMenu hideIconBackground usePortal options={options}>
      <ContractChip type="button">
        <ContractName>{contractName || t('leaveContract')}</ContractName>
        {assignmentDate && <AssignmentDate>{assignmentDate}</AssignmentDate>}
      </ContractChip>
    </ContextMenu>
  );
};

const HeaderBar = ({
  management,
  hasActiveAssignment,
  hasBalances,
  hasManualYearCycleBalance,
  onAssign,
  onChange,
  onTerminate,
  onYearReset,
  onPayoutAll,
  onEditAssignment,
  onShowLedger
}) => {
  const { t } = useContext(ConfigContext);

  if (!hasActiveAssignment) {
    return (
      <HeaderRow>
        <AssignButton
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onAssign();
          }}>
          {t('employeeLeave_assignLeaveContract')}
        </AssignButton>
      </HeaderRow>
    );
  }

  const fteRate = Number.isFinite(management?.employmentRate) ? management.employmentRate : null;

  return (
    <HeaderRow>
      <ContractHeaderChip
        management={management}
        onChange={onChange}
        onTerminate={onTerminate}
        onYearReset={onYearReset}
        onPayoutAll={onPayoutAll}
        onEditAssignment={onEditAssignment}
        hasBalances={hasBalances}
        hasManualYearCycleBalance={hasManualYearCycleBalance}
      />
      <FteChip>
        <FteLabel>{t('employeeLeave_fteRate')}</FteLabel>
        <FteValue>{fteRate !== null ? `${fteRate}%` : '-'}</FteValue>
      </FteChip>
      <HeaderSpacer />
      <LedgerHistoryButton
        type="button"
        secondary
        narrow
        onClick={(e) => {
          e.preventDefault();
          onShowLedger();
        }}>
        {t('employeeLeave_ledger_historyButton')}
      </LedgerHistoryButton>
    </HeaderRow>
  );
};

export default HeaderBar;
